import { useState, useEffect, useMemo, useCallback } from 'react';
import { ALL_GENEROS } from '../../constants/musicConstants.js';

/**
 * Un conjunto de estado de filtros/buscador (misma forma que el mercado).
 * Instancias independientes: mercado vs subastas.
 */
export function useMarketplaceFilterSlice() {
  const [busqueda, setBusqueda] = useState('');
  const [provinciaSel, setProvinciaSel] = useState('');
  const [municipioSel, setMunicipioSel] = useState('');
  const [generoInput, setGeneroInput] = useState('');
  const [generosSel, setGenerosSel] = useState([]);
  const [generoCategoria, setGeneroCategoria] = useState(null);
  const [tiposSel, setTiposSel] = useState([]);
  const [condFiltros, setCondFiltros] = useState([]);
  const [genFiltros, setGenFiltros] = useState([]);
  const [decadaFiltros, setDecadaFiltros] = useState([]);
  /** Límite superior para UI de precio (derivado del catálogo actual). */
  const [precioMaxUi, setPrecioMaxUi] = useState(300);
  const [precioRange, setPrecioRange] = useState([0, 300]);
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);
  const [ordenar, setOrdenar] = useState('recientes');
  const [savedFilters, setSavedFilters] = useState([]);
  const [nombreFiltro, setNombreFiltro] = useState('');
  const [tooltipCond, setTooltipCond] = useState(null);

  const [hideGeneroSuggestions, setHideGeneroSuggestions] = useState(false);

  /** Filtro “Cerca de mí”: requiere permiso del navegador + coordenadas en el anuncio (`lat`/`lng`). */
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [distanciaKmFiltro, setDistanciaKmFiltro] = useState(null);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [geoNearbyError, setGeoNearbyError] = useState(null);

  const desactivarFiltroDistancia = useCallback(() => {
    setUserLat(null);
    setUserLng(null);
    setDistanciaKmFiltro(null);
    setGeoNearbyError(null);
    setLoadingGeo(false);
  }, []);

  const activarFiltroDistancia = useCallback(() => {
    setGeoNearbyError(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoNearbyError('Tu navegador no permite ubicación.');
      return;
    }
    setLoadingGeo(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setDistanciaKmFiltro(15);
        setLoadingGeo(false);
      },
      err => {
        setLoadingGeo(false);
        const code = err?.code;
        if (code === 1) setGeoNearbyError('Permiso de ubicación denegado.');
        else if (code === 2) setGeoNearbyError('Ubicación no disponible.');
        else if (code === 3) setGeoNearbyError('Tiempo de espera agotado.');
        else setGeoNearbyError('No se pudo obtener tu ubicación.');
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => {
    setHideGeneroSuggestions(false);
  }, [generoInput]);

  const generosAC = useMemo(() => {
    if (hideGeneroSuggestions || !generoInput.trim()) return [];
    const q = generoInput.toLowerCase();
    return ALL_GENEROS.filter(g => g.toLowerCase().includes(q) && !generosSel.includes(g)).slice(0, 14);
  }, [generoInput, generosSel, hideGeneroSuggestions]);

  const onGeneroInputBlur = useCallback(() => {
    setTimeout(() => setHideGeneroSuggestions(true), 150);
  }, []);

  const addGenero = useCallback(g => {
    setGenerosSel(p => {
      if (p.includes(g)) return p;
      return [...p, g];
    });
    setGeneroInput('');
  }, []);

  const toggleGeneroSel = useCallback(g => {
    setGenerosSel(p => (p.includes(g) ? p.filter(x => x !== g) : [...p, g]));
  }, []);

  const removeGenero = useCallback(g => {
    setGenerosSel(p => p.filter(x => x !== g));
  }, []);

  const toggleCond = useCallback(c => {
    setCondFiltros(p => (p.includes(c) ? p.filter(x => x !== c) : [...p, c]));
  }, []);

  const toggleGen = useCallback(g => {
    setGenFiltros(p => (p.includes(g) ? p.filter(x => x !== g) : [...p, g]));
  }, []);

  const toggleDecada = useCallback(d => {
    setDecadaFiltros(p => (p.includes(d) ? p.filter(x => x !== d) : [...p, d]));
  }, []);

  const toggleTipo = useCallback(t => {
    setTiposSel(p => (p.includes(t) ? p.filter(x => x !== t) : [...p, t]));
  }, []);

  const limpiar = useCallback(() => {
    setBusqueda('');
    setProvinciaSel('');
    setMunicipioSel('');
    setGeneroInput('');
    setGenerosSel([]);
    setGeneroCategoria(null);
    setTiposSel([]);
    setCondFiltros([]);
    setGenFiltros([]);
    setDecadaFiltros([]);
    setPrecioRange([0, precioMaxUi]);
    setFiltrosAplicados(false);
    setUserLat(null);
    setUserLng(null);
    setDistanciaKmFiltro(null);
    setGeoNearbyError(null);
    setLoadingGeo(false);
  }, [precioMaxUi]);

  const ubicacionSelector = useMemo(
    () => ({
      provincia: provinciaSel,
      municipio: municipioSel,
    }),
    [provinciaSel, municipioSel]
  );

  const filters = useMemo(
    () => ({
      busqueda,
      setBusqueda,
      ubicacionSelector,
      provinciaSel,
      setProvinciaSel,
      municipioSel,
      setMunicipioSel,
      generoInput,
      setGeneroInput,
      generosSel,
      setGenerosSel,
      generoCategoria,
      setGeneroCategoria,
      tiposSel,
      setTiposSel,
      condFiltros,
      setCondFiltros,
      genFiltros,
      setGenFiltros,
      decadaFiltros,
      setDecadaFiltros,
      precioMaxUi,
      setPrecioMaxUi,
      precioRange,
      setPrecioRange,
      filtrosAplicados,
      setFiltrosAplicados,
      ordenar,
      setOrdenar,
      savedFilters,
      setSavedFilters,
      nombreFiltro,
      setNombreFiltro,
      generosAC,
      userLat,
      userLng,
      distanciaKmFiltro,
      setDistanciaKmFiltro,
      loadingGeo,
      geoNearbyError,
    }),
    [
      busqueda,
      ubicacionSelector,
      provinciaSel,
      municipioSel,
      generoInput,
      generosSel,
      generoCategoria,
      tiposSel,
      condFiltros,
      genFiltros,
      decadaFiltros,
      precioMaxUi,
      precioRange,
      filtrosAplicados,
      ordenar,
      savedFilters,
      nombreFiltro,
      generosAC,
      userLat,
      userLng,
      distanciaKmFiltro,
      loadingGeo,
      geoNearbyError,
    ]
  );

  const actions = useMemo(
    () => ({
      limpiar,
      toggleTipo,
      addGenero,
      toggleGeneroSel,
      removeGenero,
      toggleCond,
      toggleGen,
      toggleDecada,
      onGeneroInputBlur,
      activarFiltroDistancia,
      desactivarFiltroDistancia,
    }),
    [
      limpiar,
      toggleTipo,
      addGenero,
      toggleGeneroSel,
      removeGenero,
      toggleCond,
      toggleGen,
      toggleDecada,
      onGeneroInputBlur,
      activarFiltroDistancia,
      desactivarFiltroDistancia,
    ]
  );

  return { filters, actions, tooltipCond, setTooltipCond };
}
